import { useCallback, useEffect } from 'react';
import { User } from 'lucide-react';
import { DocumentVerificationStatusEnum } from 'api/actor';
import { contractsAPI } from 'services/swagger-api';
import { Sizes } from 'enums/sizes';
import { LightContractCard } from 'features/subscription-process/contractualization-process/components/cards/LightContractCard';
import { CardState } from 'features/subscription-process/contractualization-process/types/cardState';
import { PersonWithDocuments } from 'features/new-actor/components/PersonWithDocuments';
import { useFetchPersonWithDocuments } from 'features/new-actor/hooks/useFetchPersonWithDocuments';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { NsKey } from 'enums/NsKey';
import { useGroupContractProcess } from '../../contexts/GroupContractProcessContext';
import { SubscriberPicker } from '../../components/SubscriberPicker';
import { GroupContractScope } from '../../api/groupContractSubscribersApi';
import { SubscriberLockedSummary } from './SubscriberLockedSummary';
import { ViewDetailsButton } from '../../components/ViewDetailsButton';

interface PickerPanelContentProps {
  contractId: string;
  scope: GroupContractScope;
  onPersonReady: (personId: string) => Promise<void>;
}
const PickerPanelContent = ({ contractId, scope, onPersonReady }: PickerPanelContentProps) => (
  <SubscriberPicker
    contractId={contractId}
    scope={scope}
    initialPerson={null}
    onPersonReady={onPersonReady}
  />
);

interface DocumentsPanelContentProps {
  personId: string;
  onSuccess: () => void;
  onDocumentChange: () => Promise<void>;
}
const DocumentsPanelContent = ({ personId, onSuccess, onDocumentChange }: DocumentsPanelContentProps) => (
  <PersonWithDocuments
    personId={personId}
    onSuccess={onSuccess}
    onDocumentChange={onDocumentChange}
  />
);

interface SubscriberSummaryCardProps {
  /** Notifie le parent quand la card devient complète (souscripteur + pièces valides). */
  onValidityChange?: (isValid: boolean) => void;
}

export const SubscriberSummaryCard = ({ onValidityChange }: SubscriberSummaryCardProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  // Le contrat vient du contexte (source unique, rafraîchie en place par refresh()).
  const { groupContractId, contract, isLocked, refresh } = useGroupContractProcess();
  const { openPanel, closePanel, setPanelActions } = usePanel();

  const contractLoading = contract == null;

  const subscriber = contract?.subscriber as { id?: string; name?: string } | undefined;
  const subscriberId = subscriber?.id ?? '';
  const subscriberName = subscriber?.name ?? null;

  // Charge la personne + ses documents pour évaluer la complétude (CNI vérifiée / KBIS fourni).
  const { personType, documents, isLoading: personLoading, refetch: refetchPerson } =
    useFetchPersonWithDocuments(subscriberId);

  // Pièce d'identité considérée OK :
  //  - personne physique : CNI uploadée ET vérifiée
  //  - personne morale  : KBIS uploadé (pas de notion de vérification ici)
  const isIdValid = !!(
    subscriberId
    && (
      (personType === 'physique' && documents.cni?.verificationStatus === DocumentVerificationStatusEnum.Verified)
      || (personType === 'morale' && documents.kbis?.storedFileId)
    )
  );

  const isComplete = !!subscriberId && isIdValid;

  // Notifie le parent (Contractualisation) pour bloquer les signatures.
  useEffect(() => {
    onValidityChange?.(isComplete);
  }, [isComplete, onValidityChange]);

  const cardState: CardState = isComplete ? CardState.VALID : CardState.CRITICAL;

  // Pas de subtitle : aligné sur le pattern simulation (juste le nom en titre).

  // Vue 1 : pas de souscripteur → ouvre le picker pour en choisir/créer un.
  const handlePickerReady = useCallback(async (personId: string) => {
    await contractsAPI.setGroupContractSubscriber({
      groupContractId,
      setSubscriberCommand: { subscriberId: personId },
    });
    await refresh();
    closePanel();
  }, [groupContractId, refresh, closePanel]);

  const handleOpen = useCallback(() => {
    // Verrouillé → panel read-only (résumé), sans footer d'actions.
    if (isLocked) {
      openPanel(
        {
          title: t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_CARD.PANEL_TITLE'),
          size: Sizes['3xl'],
        },
        <SubscriberLockedSummary groupContractId={groupContractId} />,
      );
      setPanelActions(undefined);
      return;
    }

    if (!subscriberId) {
      const scope = (contract as { scope?: GroupContractScope } | null)?.scope
        ?? GroupContractScope.PersonneMorale;
      openPanel(
        {
          title: t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_CARD.PANEL_TITLE'),
          size: Sizes['3xl'],
        },
        <PickerPanelContent
          contractId={groupContractId}
          scope={scope}
          onPersonReady={handlePickerReady}
        />,
      );
      return;
    }
    // Vue 2 : souscripteur existant → édition full (form + documents) via PersonWithDocuments.
    openPanel(
      {
        title: t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_CARD.PANEL_TITLE'),
        size: Sizes['3xl'],
      },
      <DocumentsPanelContent
        personId={subscriberId}
        onSuccess={() => { Promise.all([refresh(), refetchPerson()]); }}
        onDocumentChange={async () => {
          await refetchPerson();
        }}
      />,
    );
  }, [
    isLocked,
    subscriberId,
    contract,
    groupContractId,
    openPanel,
    setPanelActions,
    t,
    handlePickerReady,
    refresh,
    refetchPerson,
  ]);

  return (
    <LightContractCard
      className={isLocked ? 'hover:!shadow-none' : undefined}
      icon={<User className="w-7 h-7 text-primary" />}
      title={
        contractLoading || (subscriberId && personLoading)
          ? t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_CARD.LOADING')
          : (subscriberName ?? t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_CARD.EMPTY'))
      }
      status={cardState}
      onClick={isLocked ? undefined : handleOpen}
      titleAsideOverride={isLocked ? <ViewDetailsButton onClick={handleOpen} /> : undefined}
    />
  );
};
